import React, { useCallback, memo, useContext } from 'react';
import { View, RefreshControl } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { selectAccountShelves, selectBooksForShelves } from '../../selectors';
import { getShelvesApi, getShelvesInProgress } from '../../actions/getShelves';
import { useIsLoading, useGetDataOnNavFocus, useAskForFeedback } from '../../hooks';
import { resetShelvesFilter } from '../../actions';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BookListHor from '../../components/BookListHor';
import BookListHorHeader from '../../components/BookListHorHeader';
import Empty from '../../components/Empty';
import FilterBooks, { FILTER_HEIGHT } from '../../components/FilterBooks';
import { isWeb, isLargeScreen, isAndroid, isIOS } from '../../utils';
import Animated, { useSharedValue, useAnimatedScrollHandler } from 'react-native-reanimated';
import AppSettingsContext from '../../context/AppSettingsContext';

const tBase = 'screens.shelves';
const ADD_BOOK_SCREEN = isWeb() ? 'AddBookSearch' : 'Scan';

export default function ShelvesScreen({ navigation }) {
    const {t} = useContext(AppSettingsContext);
    const dispatch = useDispatch();
    const scrollY = useSharedValue(0);
    const pattern = useSelector(state => state.settings.filters.shelves.pattern);
    const shelves = useSelector(selectAccountShelves);
    const booksForShelves = useSelector(selectBooksForShelves);
    const insets = useSafeAreaInsets();
    const isGettingShelves = useIsLoading(getShelvesInProgress);
    const getShelves = (checkCaching=true) => {
        !isGettingShelves && dispatch(getShelvesApi({ showLoading: false, checkCaching }))
    }
    useGetDataOnNavFocus(navigation, getShelves);
    const clearShelvesFilter = () => {
        dispatch(resetShelvesFilter());
    }
    const paddingBottom = (() => {
        if(isLargeScreen()) return insets.bottom + 50;
        return 50;
    })()
    const hasOneShelfAndNoBooks = shelves.length == 1 && shelves[0].bookIds.length == 0;
    const noResultsFromFilter = (() => {
        let hasResults = false;
        for(let [key, value] of Object.entries(booksForShelves)) {
            if(value.length) {
                hasResults = true;
                break;
            }
        }
        return !hasResults && pattern;
    })();
    
    const scrollHandler = useAnimatedScrollHandler({
        onScroll: (e) => {
            scrollY.value = e.contentOffset.y;
        },
    })
    useAskForFeedback(navigation, t);
    return (
        <>
            <FilterBooks tBase={tBase} scrollY={scrollY} />
            { noResultsFromFilter ?
                <Empty 
                    title={t(`${tBase}.noResults.title`)}
                    body={t(`${tBase}.noResults.body`, { pattern })}
                    primaryActionLabel={ t(`${tBase}.noResults.showAll`)}
                    primaryActionOnPress={clearShelvesFilter} /> :
                <Animated.ScrollView
                    onScroll={scrollHandler}
                    scrollEventThrottle={16}
                    contentContainerStyle={{ flexGrow: 1, paddingTop: isIOS() ? 10 : 10 + FILTER_HEIGHT, paddingBottom }}
                    contentInset={{ top: isIOS() ? FILTER_HEIGHT : 0 }}
                    contentOffset={{ x: 0, y: isIOS() ?  -FILTER_HEIGHT : 0 }}
                    keyboardShouldPersistTaps="handled"
                    refreshControl={
                        <RefreshControl
                            refreshing={isGettingShelves}
                            progressViewOffset={ isAndroid() ? FILTER_HEIGHT : 0 }
                            onRefresh={() => getShelves(false)}
                        /> 
                    }>
                    { hasOneShelfAndNoBooks ?
                        <Empty 
                            title={ t(`${tBase}.empty.title`) }
                            body={ t(`${tBase}.empty.body`) }
                            img={ require('../../assets/emptyLibrary.png') }
                            primaryActionLabel={ t(`${tBase}.empty.addBooks`) }
                            primaryActionOnPress={ () => navigation.navigate('Modal', { screen: ADD_BOOK_SCREEN }) } /> :
                        <Shelves 
                            shelves={shelves} 
                            booksForShelves={booksForShelves} 
                            navigation={navigation} />
                    }
                </Animated.ScrollView>
            }
        </>
    )
}

const Shelves = memo(({ shelves, booksForShelves, navigation }) => {
    return (
        shelves.map((shelf, index) => (
            <Shelf 
                key={shelf.id} 
                shelf={shelf}
                books={booksForShelves[shelf.id]} 
                index={index} 
                navigation={navigation} />
        ))
    )
})

const Shelf = memo(({ shelf, books, index, navigation }) => {
    const pattern = useSelector(state => state.settings.filters.shelves.pattern);
    const navigateToShelf = useCallback(() => navigation.push('Shelf', { shelfId: shelf.id }), []);
    if(!books.length && pattern) return <View />;
    return (
        <View style={{ marginTop: 0 }}>
            <BookListHorHeader
                tBase={tBase} 
                title={shelf.name} 
                isPrivate={shelf.visibility == 'private'}
                count={books.length} 
                isFirst={index === 0}
                onHeaderPress={navigateToShelf} />
            <BookListHor 
                books={books} 
                onSeeAllPress={navigateToShelf}
                navigation={navigation} />
        </View>
    )
})